import React from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import InputBase from '@mui/material/InputBase';
import CircularProgress from '@mui/material/CircularProgress';
import Autocomplete, {
  AutocompleteProps,
  AutocompleteValue,
} from '@mui/material/Autocomplete';
import SearchIcon from '@mui/icons-material/Search';
import CancelIcon from '@mui/icons-material/Cancel';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import { NATION_WIDE } from '@/constants/city';

// SearchChip is a component for user to select multiple value returned by api and it support autocompleting
// the component limit user to select the value provided by api, so freeSolo must be false

interface SearchChipProps<T, DisableClearable extends boolean | undefined>
  extends Omit<
    AutocompleteProps<T, true, DisableClearable, false>,
    'onChange' | 'options' | 'renderInput' | 'multiple'
  > {
  value: AutocompleteValue<T, true, DisableClearable, false>;
  label?: string;
  getOptions: (query: string) => Promise<{ data: T[] }>;
  placeholder?: string;
  onChange: (
    value: AutocompleteValue<T, true, DisableClearable, false>,
  ) => void;
  inputSx?: React.CSSProperties;
  limit?: number;
  error?: boolean;
  helperText?: string | React.ReactNode;
  isSearchCity?: boolean;
}

const SearchChip = <
  T,
  DisableClearable extends boolean | undefined = undefined,
>(
  props: SearchChipProps<T, DisableClearable>,
) => {
  const {
    value,
    label = '',
    placeholder = '',
    getOptions,
    className = '',
    onChange,
    inputSx = {},
    getOptionLabel,
    noOptionsText = '暂无匹配结果',
    limit,
    sx = {},
    disabled = false,
    error = false,
    helperText = '',
    isSearchCity = false,
  } = props;
  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState<readonly T[]>([]);
  const [inputValue, setInputValue] = React.useState('');
  const [focused, setFocused] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const handleSearch = React.useCallback(
    debounce(async (query, active) => {
      const response = await getOptions(query);
      if (active) {
        setLoading(false);
        const data = response.data;
        if (Array.isArray(data) && isSearchCity) {
          data.unshift(NATION_WIDE as any);
          setOptions(data);
        } else {
          setOptions(data);
        }
      }
    }, 300),
    [],
  );

  React.useEffect(() => {
    let active = true;

    if (!open) {
      setOptions([]);
      return;
    }
    if (!inputValue) {
      setOpen(false);
      return;
    }
    setOptions(() => {
      setLoading(true);
      return [];
    });
    handleSearch(inputValue, active);

    return () => {
      active = false;
    };
  }, [open, inputValue]);

  const handleInputChange = (
    event: React.SyntheticEvent,
    inputValue: string,
  ) => {
    setInputValue(inputValue);
  };

  const handleChipDelete = (option: T) => {
    const newValue = value.filter((item) => item !== option);
    onChange(newValue);
  };

  const getChipLabel = (option: T) => {
    if (getOptionLabel) return getOptionLabel(option);
    if (typeof option === 'string') return option;
    throw new Error(
      "Option type isn't string and don't provide getOptionLabel method.",
    );
  };

  return (
    <Box className={`flex flex-col ${className}`} sx={sx}>
      {label && <label className="text-sm font-semibold mb-4">{label}</label>}
      <Autocomplete<T, true, DisableClearable, false>
        multiple
        disabled={disabled}
        options={options}
        value={value}
        inputValue={inputValue}
        open={open}
        noOptionsText={noOptionsText}
        loading={loading}
        loadingText={<CircularProgress color="inherit" size={24} />}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onInputChange={handleInputChange}
        onChange={(event, value) => onChange(value)}
        getOptionLabel={getOptionLabel}
        filterOptions={(options) => options}
        isOptionEqualToValue={(option: T, value: T) => isEqual(option, value)}
        getOptionDisabled={() =>
          limit !== undefined ? value.length >= limit : false
        }
        renderInput={(params) => (
          <Box
            ref={params.InputProps.ref}
            className={`flex items-center justify-between rounded relative bg-slate-50 text-sm w-auto py-3.5 px-4 ${
              focused ? 'focused' : 'unFocused'
            }`}
            sx={{
              maxWidth: 540,
              '&:before': {
                left: 0,
                bottom: 0,
                content: '""',
                position: 'absolute',
                right: 0,
                transform: 'scaleX(0)',
                pointerEvents: 'none',
                borderBottom: error ? '2px solid #b91c1c' : '2px solid #2563eb',
              },
              '&.focused:before': {
                transition: '.2s ease-in-out',
                transform: 'scaleX(1)',
              },
              '&.unFocused:before': {
                transition: '.2s ease-in-out',
                transform: 'scaleX(0)',
              },
              '&:hover': {
                backgroundColor: '#f1f5f9',
              },
              ...inputSx,
            }}
          >
            <div className="flex flex-wrap gap-2 content-start w-max">
              {value.map((option, index: number) => (
                <Chip
                  key={index}
                  label={getChipLabel(option)}
                  deleteIcon={<CancelIcon />}
                  onDelete={() => handleChipDelete(option)}
                  sx={{
                    marginRight: '6px',
                    height: '30px',
                    backgroundColor: '#0050b3',
                    color: 'white',
                    borderRadius: '20px',
                    '& .MuiChip-deleteIcon': {
                      color: 'white',
                    },
                    '& .MuiChip-deleteIcon:hover': {
                      color: 'white',
                    },
                  }}
                />
              ))}
              <InputBase
                sx={{
                  color: '#000',
                  lineHeight: 1.5,
                }}
                placeholder={placeholder}
                className={`flex-auto text-sm ${
                  limit && value.length >= limit ? 'hidden' : ''
                }`}
                inputProps={params.inputProps}
              />
            </div>
            <SearchIcon sx={{ color: '#79747e' }} />
          </Box>
        )}
      />
      {error && helperText && (
        <span className="text-red-700 text-xs mt-2">{helperText}</span>
      )}
    </Box>
  );
};

export default SearchChip;
